/** @jsxImportSource @emotion/react */
import tw from "twin.macro";
import {
  addDefaultDuration,
  addDurationOfNote,
  computeGroups,
  Group,
  putInExactSchedule,
  saveDuration,
} from "../state/data/utils";
import { css } from "@emotion/css";
import { ScaleTime, scaleTime } from "d3-scale";
import {
  addDays,
  addHours,
  addMinutes,
  areIntervalsOverlapping,
  compareDesc,
  differenceInMinutes,
  formatISO,
  isEqual,
  max,
  parseISO,
  startOfDay,
  startOfToday,
  subHours,
  subMinutes,
} from "date-fns";
import {
  useObservable,
  useObservableState,
  useSubscription,
} from "observable-hooks";
import * as R from "ramda";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { animated, useSpring, useSprings } from "react-spring";
import { useDrag, useHover } from "react-use-gesture";
import useMeasure from "react-use-measure";
import { combineLatest, interval, Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { DayColumn } from "./Calendar";
import { ExactlyScheduledNote, ExactSchedule, Note } from "../state/data";
import {
  noteCreator,
  useNotes,
  useNotesOperations,
} from "../state/state/store";
import { DraggableNote } from "../view/drag/DraggableNote";
import {
  animationEndings$,
  ongoingDrag$,
  isAnimating$,
  placeholderDimensions$,
  draggedNote$,
} from "../view/drag/Control";
import { Placeholder } from "../view/drag/Placeholder";
import { dbg } from "../utils";
import { DELTA_Y_WHILE_DRAGGING } from "../view/drag/DraggedNote";
import mergeRefs from "react-merge-refs";
import { HourRuler } from "./HourRuler";
import { Resizable } from "re-resizable";
import {
  processPlaceholderScheduling,
  scheduleFromResize,
  displaceFromExactSchedule,
} from "./layoutUtils";
import { DayColumnHeader } from "./ColumnHeader";
import { useDayHeight, useDayWidth } from "../state/state/viewOptionsStore";
import firebase from "firebase/app";
import {
  CalendarEvent,
  useCalendarEvents,
} from "../state/state/useCalendarEvents";
import styled from "@emotion/styled";

const NOTE_MIN_HEIGHT = 30;
const HOUR_RULER_WIDTH = 45;
const LEFT_MARGIN = 25;
const CAL_EVENTS_WIDTH = 0;

type DropExistingEvent = {
  type: "DROP_EXISTING";
  id: string;
  y: number;
};

type CreateNewEvent = {
  type: "CREATE_NEW";
  start: number;
  end: number;
};

type ConfirmNew = {
  type: "CONFIRM_NEW";
};

type ResetEvent = {
  type: "RESET";
};

type CalendarUIEvent =
  | DropExistingEvent
  | ResetEvent
  | CreateNewEvent
  | ConfirmNew;

const NotesOnCalendar = ({
  day,
  scale,
}: {
  day: Date;
  scale: ScaleTime<number, number, never>;
}) => {
  const notes = useNotes().notes as ExactlyScheduledNote[];
  const dayNotes = R.sortBy(
    (n) => formatISO(n.schedule.start),
    notes.filter((note) => {
      return (
        note.schedule.type === "EXACT" &&
        areIntervalsOverlapping(
          {
            start: day,
            end: addDays(day, 1),
          },
          {
            start: note.schedule.start,
            end: note.schedule.end,
          }
        )
      );
    })
  );

  return <InnerNotesOnCalendar notes={dayNotes} scale={scale} />;
};

const InnerNotesOnCalendar = ({
  notes,
  scale,
}: {
  notes: ExactlyScheduledNote[];
  scale: ScaleTime<number, number, never>;
}) => {
  const notesOperations = useNotesOperations();
  const dayWidth = useDayWidth();
  const colWidth =
    dayWidth - HOUR_RULER_WIDTH - CAL_EVENTS_WIDTH - LEFT_MARGIN - 8;
  console.log("rendering with", notes.length, notes);
  // Note Springs
  const baseInitialNoteSprings = (opt, currentNotes, index) => {
    // console.log("index", index, currentNotes);
    if (currentNotes[index] === undefined) {
      return { width: 0, x: 0 };
    }
    return {
      width: currentNotes[index].schedule.width * colWidth,
      x: currentNotes[index].schedule.offset * colWidth,
      ...opt,
    };
  };
  const initialNoteSprings = (i: number) =>
    baseInitialNoteSprings({}, notes, i);
  const [springs, setSprings] = useSprings(notes.length, initialNoteSprings, [
    notes,
  ]);

  // Placeholder Positioning
  const [phSpring, setPhSpring] = useSpring(() => ({
    x: 0,
    top: 0,
    height: 0,
    width: 0,
    immediate: true,
  }));
  const [phActive, setPhActive] = useState<boolean>(false);

  const phScheduleRef = useRef<ExactSchedule | undefined>(undefined);
  const displacedNotesRef = useRef<ExactlyScheduledNote[]>([]);

  const savePlaceholderDimensions = ({
    start,
    end,
    width,
    offset,
  }: {
    start: Date;
    end: Date;
    width: number;
    offset: number;
  }) => {
    // Saving for logic
    //@ts-ignore
    phScheduleRef.current = { type: "EXACT", start, end, width, offset };

    // Side effects for Layout

    const layoutWidth = width * colWidth;
    const layoutHeight = scale(end) - scale(start);
    placeholderDimensions$.next({ width: layoutWidth, height: layoutHeight });
    setPhSpring({
      x: offset * colWidth,
      top: scale(start),
      width: layoutWidth,
      height: layoutHeight,
      immediate: true,
    });
  };

  const saveDisplacedNotes = (displaced: Note[]) => {
    //@ts-ignore
    displacedNotesRef.current = displaced;
  };

  // Hover / Drop events listening, position computation
  const ce$ = new Subject<CalendarUIEvent>();

  const cleanUpPlaceholderState = () => {
    setPhActive(false);
    setSprings.start(initialNoteSprings);
    displacedNotesRef.current = [];
  };

  const ongoingDrag = useObservableState(ongoingDrag$);
  const draggedNote = useObservableState(draggedNote$);

  const groupsRef = useRef<Group[]>();
  useEffect(() => {
    groupsRef.current = computeGroups(
      notes.filter((n) => n.id !== ongoingDrag?.id)
    );
  }, [notes, ongoingDrag]);

  ce$.subscribe({
    next: (e) => {
      if (e.type === "RESET") {
        cleanUpPlaceholderState();
      }
      if (e.type === "DROP_EXISTING") {
        setPhActive(true);
        const scheduleTo = (() => {
          const start = scale.invert(e.y - DELTA_Y_WHILE_DRAGGING - 8);
          return {
            start,
            end: addDurationOfNote(draggedNote!)(start),
          };
        })();

        const { schedule, springSetter, displaced } =
          processPlaceholderScheduling(
            scheduleTo,
            groupsRef.current!,
            notes,
            colWidth
          );
        savePlaceholderDimensions(schedule);
        setSprings.start(springSetter ?? initialNoteSprings);
        saveDisplacedNotes(displaced ?? []);
      }
      if (e.type === "CREATE_NEW") {
        setPhActive(true);

        const start = scale.invert(e.start);
        const end = scale.invert(e.end);

        const scheduleTo = (() => {
          if (compareDesc(end, addDefaultDuration(start)) < 0) {
            return {
              start,
              end,
            };
          } else {
            return {
              start,
              end: addDefaultDuration(start),
            };
          }
        })();

        const { schedule, springSetter, displaced } =
          processPlaceholderScheduling(
            scheduleTo,
            groupsRef.current!,
            notes,
            colWidth
          );
        savePlaceholderDimensions(schedule);
        setSprings.start(springSetter ?? initialNoteSprings);
        saveDisplacedNotes(displaced ?? []);
      }

      if (e.type === "CONFIRM_NEW") {
        const newnote = putInExactSchedule(phScheduleRef.current!)(
          noteCreator()
        );
        notesOperations.addNote(newnote);
        displacedNotesRef.current.forEach((n) => notesOperations.updateNote(n));
        cleanUpPlaceholderState();
        firebase.analytics().logEvent("note_created");
      }
    },
  });

  useSubscription(animationEndings$, (e) => {
    if (!phActive || phScheduleRef.current === undefined) {
      // drop not in component
      return;
    }
    setPhActive(false);
    notesOperations.updateNoteById(
      e.id,
      putInExactSchedule(phScheduleRef.current)
    );
    displacedNotesRef.current.forEach((n) => notesOperations.updateNote(n));
  });

  // Resize
  const [resizeSpring, setResizeSpring] = useSpring(() => ({ x: 0, y: 0 }));
  const [resizingNote, setResizingNote] = useState<undefined | string>(
    undefined
  );

  const springging = phActive || resizingNote !== undefined;

  return (
    <>
      <div
        className={css`
          margin-left: ${LEFT_MARGIN}px;
        `}
      >
        {notes.map((n, i) => {
          return (
            <animated.div
              key={n.id}
              className={css`
                position: absolute;
                top: ${scale(n.schedule.start)}px;
                height: ${scale(n.schedule.end) - scale(n.schedule.start)}px;
                z-index: 10;
                &:focus-within {
                  z-index: 15;
                }
              `}
              style={{
                width: n.schedule.width * colWidth + "px",
                transform:
                  springging && resizingNote !== n.id
                    ? `translate3d(0, 0, 0)`
                    : `translate3d(${n.schedule.offset * colWidth}px, 0, 0)`,
                ...(springging && resizingNote !== n.id ? springs[i] : {}),
                ...(resizingNote === n.id ? resizeSpring : {}),
              }}
            >
              <Resizable
                size={{ height: "100%", width: "100%" }}
                minHeight={NOTE_MIN_HEIGHT + "px"}
                onResizeStart={() => {
                  setResizingNote(n.id);
                }}
                onResize={(e, direction, el, size) => {
                  //Translate to compensate for size increase/decrease if top/left resize
                  if (R.includes(direction, ["top", "topRight", "topLeft"])) {
                    setResizeSpring({ y: -size.height, immediate: true });
                  }

                  if (
                    R.includes(direction, ["left", "topLeft", "bottomLeft"])
                  ) {
                    setResizeSpring({ x: -size.width, immediate: true });
                  }

                  // displace stuff around
                  const newSchedule = scheduleFromResize(
                    direction,
                    size,
                    n,
                    scale,
                    colWidth
                  );

                  // console.log("new schedule = ", newSchedule);

                  const { springSetter, displaced } = displaceFromExactSchedule(
                    newSchedule,
                    notes,
                    groupsRef.current!,
                    colWidth
                  );
                  // savePlaceholderDimensions(schedule);
                  setSprings.start(springSetter ?? initialNoteSprings);
                  saveDisplacedNotes(displaced ?? []);
                }}
                onResizeStop={(e, direction, el, size) => {
                  const newSchedule = scheduleFromResize(
                    direction,
                    size,
                    n,
                    scale,
                    colWidth
                  );

                  displacedNotesRef.current.forEach((n) =>
                    notesOperations.updateNote(n)
                  );

                  notesOperations.updateNoteById(
                    n.id,
                    R.compose(saveDuration, putInExactSchedule(newSchedule))
                  );

                  setResizingNote(undefined);
                  setResizeSpring({ x: 0, y: 0, immediate: true });
                }}
                //@ts-ignore
                handleClasses={{
                  top: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  bottom: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  left: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  right: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  topRight: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  topLeft: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  bottomRight: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                  bottomLeft: css`
                    /* border: 1px solid green; */
                    z-index: 85;
                  `,
                }}
              >
                <DraggableNote id={n.id} controlledHeight={true} />
              </Resizable>
            </animated.div>
          );
        })}
      </div>
      {phActive && (
        <animated.div
          key={"drop-placeholder"}
          className={css`
            position: absolute;
            z-index: 5;
            margin-left: ${LEFT_MARGIN}px;
          `}
          style={{
            ...phSpring,
          }}
        >
          <Placeholder height={"100%"} />
        </animated.div>
      )}
      <MouseOverlay ce$={ce$} scale={scale} />
    </>
  );
};

const MouseOverlay = ({
  ce$,
}: {
  ce$: Subject<CalendarUIEvent>;
  scale: ScaleTime<number, number, never>;
}) => {
  const [ref, bounds] = useMeasure({ scroll: true });
  const [creating, setCreating] = useState(false);

  const creationDrag = useDrag(
    ({ event, down, movement: [mx, my], initial: [ix, iy] }) => {
      if (down && (event.buttons === 4 || event.buttons === 2)) {
        // Ignore non left click
        return;
      }

      const a = iy - bounds.y;
      const b = iy - bounds.y + my;

      if (down) {
        setCreating(true);
        ce$.next({
          type: "CREATE_NEW",
          start: Math.min(a, b),
          end: Math.min(a, b) + Math.abs(a - b),
        });
      }
      if (!down && creating) {
        setCreating(false);
        ce$.next({
          type: "CONFIRM_NEW",
        });
      }
    }
  );

  const ongoingDrag = useObservableState(ongoingDrag$);
  const isAnimating = useObservableState(isAnimating$);
  return (
    <>
      <div
        className={css`
          position: absolute;
          top: 0;
          height: 100%;
          width: 100%;
          z-index: ${ongoingDrag !== undefined ? 30 : 5};
          cursor: ${ongoingDrag !== undefined ? "grabbing" : "cell"};
        `}
        ref={ref}
        {...creationDrag()}
        onMouseMove={(e) => {
          if (ongoingDrag !== undefined) {
            ce$.next({
              type: "DROP_EXISTING",
              id: ongoingDrag.id,
              y: e.clientY - bounds.y - DELTA_Y_WHILE_DRAGGING + 33,
            });
          }
        }}
        onMouseLeave={(e) => {
          if (isAnimating) {
            return;
          }
          ce$.next({ type: "RESET" });
        }}
      ></div>
    </>
  );
};

const NowLine = ({ scale }: { scale: ScaleTime<number, number, never> }) => {
  const pong$ = useObservable(() => interval(60 * 1000));
  const ping = useObservableState(pong$);
  return (
    <div
      className={css`
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        border-top: 2px solid red;
      `}
      style={{
        transform: `translate3d(0, ${scale(new Date())}px, 0)`,
      }}
    ></div>
  );
};

const HourTicks = ({ scale }: { scale: ScaleTime<number, number, never> }) => {
  return (
    <>
      {scale.ticks(96).map((d, i) => (
        <div
          key={i}
          className={css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
          `}
          style={{
            transform: `translate3d(0, ${scale(d)}px, 0)`,
          }}
        >
          <div
            className={
              i % 4 === 0
                ? css`
                    border-top: 1.5px solid #cacdd3;
                  `
                : css`
                    border-top: 1.5px solid #ebeceec5;
                  `
            }
          ></div>
        </div>
      ))}
    </>
  );
};

export const DayBodyColumn = ({ col }: { col: DayColumn }) => {
  const dayStart = col.d;
  const dayEnd = addDays(col.d, 1);

  const dayHeight = useDayHeight();

  const scale = scaleTime().domain([dayStart, dayEnd]).range([0, dayHeight]);
  const isToday = isEqual(startOfToday(), col.d);

  return (
    <div
      className={css`
        position: relative;
        height: ${dayHeight}px;
      `}
    >
      <div
        tw="w-full bg-white sticky top-0 z-50 border-gray-300 border-b"
        style={{ height: "26rem" }}
        className={css`
          border-top: 1px solid rgba(0, 0, 0, 0.202);
        `}
      >
        <DayColumnHeader d={col.d}></DayColumnHeader>
      </div>

      <div
        tw="h-full z-40 sticky left-0 bg-white"
        style={{ width: HOUR_RULER_WIDTH + "px" }}
      >
        <HourRuler dayHeight={dayHeight} isToday={isToday} />
      </div>
      <div
        tw="absolute w-full"
        style={{ height: dayHeight, top: "26rem", overflow: "hidden" }}
      >
        <HourTicks scale={scale} />
        {isToday && <NowLine scale={scale} />}
        {/* <div style={{ marginLeft: HOUR_RULER_WIDTH + 2 + "px" }}>
          <EventsOnCalendar day={col.d} scale={scale} />
        </div> */}
        <div style={{ marginLeft: HOUR_RULER_WIDTH + 0 + 2 + "px" }}>
          <NotesOnCalendar day={col.d} scale={scale} />
        </div>
      </div>
    </div>
  );
};

const EventOnCalendarWrapper = styled.div`
  background-color: #303f9f;
  color: white;
  width: ${CAL_EVENTS_WIDTH}px;
  height: 100%;
`;

const EventSummary = styled.div`
  transform: rotate(-90deg);
  white-space: nowrap;
  position: relative;
  top: 92%;
`;

const EventOnCalendar = ({ event }: { event: CalendarEvent }) => {
  return (
    <EventOnCalendarWrapper title={event.summary}>
      <EventSummary>{event.summary}</EventSummary>
    </EventOnCalendarWrapper>
  );
};

const EventsOnCalendar = ({
  scale,
  day,
}: {
  day: Date;
  scale: ScaleTime<number, number, never>;
}) => {
  const events = useCalendarEvents(day, addDays(day, 1));
  console.log("events =", events);
  return (
    <div>
      {events.map((e) => (
        <div
          style={{
            position: "absolute",
            transform: `translate3d(0, ${scale(e.schedule.start)}px, 0)`,
            height: `${scale(e.schedule.end) - scale(e.schedule.start)}px`,
            // width: "40px",
          }}
          key={e.summary}
        >
          <EventOnCalendar event={e} />
        </div>
      ))}
    </div>
  );
};
